// React imports
import React, { useContext } from 'react'

// Context imports
import { BullGridContext } from '../../contexts/BullGridContext'
import { ThemeContext } from '../../contexts/ThemeContext'

// Package imports
import { Form, Button, InputGroup, Alert } from 'react-bootstrap'
import BullSlider from '../bull-slider/BullSlider'

// Css
import '../../stylesheets/BullFilter.css'

function CustomIndexWeights() {
    const { customIndexWeights, customIndexTraitOrder, updateCustomIndexWeight, resetCustomIndexWeights, useCustomIndex, updateUseCustomIndex, handleSliderInputChange, bullFilter } = useContext(BullGridContext);
    const { theme } = useContext(ThemeContext);

    // Friendly labels for traits that don't match the exact filter control names
    const aliasLabels = {
        PtaMilk: 'PTA Milk',
        PtaFat: 'PTA Fat',
        PtaProtein: 'PTA Protein',
        PtaFinalScore: 'PTA FS - Final Score',
        PtaStature: 'PTA ST - Stature',
        PtaStrength: 'PTA SR - Strength',
        PtaDairyForm: 'PTA DF - Dairy Form',
        PtaRumpAngle: 'PTA RA - Rump Angle',
        PtaThurlWidth: 'PTA RW - Rump Width',
        PtaRearLegs: 'PTA RL - Rear Legs',
        PtaFootAngle: 'PTA FA - Foot Angle',
        PtaForeUdder: 'PTA FU - Fore Udder',
        PtaRearUdderHeight: 'PTA RUH - Rear Udder Height',
        PtaRearUdderWidth: 'PTA RUW - Rear Udder Width',
        PtaUdderCleft: 'PTA UC - Udder Cleft',
        PtaUdderDepth: 'PTA UD - Udder Depth',
        PtaTeatPlacement: 'PTA TP - Teat Placement',
        PtaTeatLength: 'PTA TL - Teat Length',
        PtaRearTeatRearView: 'PTA RTR - Rear Teat Rear View',
        PtaRearTeatSideView: 'PTA RTS - Rear Teat Side View',
        PtaDpr: 'PTA DPR - Daughter Pregnancy Rate',
        PtaScs: 'PTA SCS - Somatic Cell Score',
        PtaProdLife: 'PTA PL - Productive Life',
        PtaCcr: 'PTA CCR - Cow Conception Rate',
        PtaHcr: 'PTA HCR - Heifer Conception Rate',
        PtaLiv: 'PTA LIV - Livability',
        PtaHti: 'HTI - Health Trait Index',
        PtaMfv: 'PTA MFV - Milk Fever',
        PtaDab: 'PTA DAB - Displaced Abomasum',
        PtaKet: 'PTA KET - Ketosis',
        PtaMas: 'PTA MAS - Mastitis',
        PtaMet: 'PTA MET - Metritis',
        PtaRpl: 'PTA RPL - Retained Placenta',
        PtaEcm: 'ECM - Energy Corrected Milk',
        PtaJui: 'PTA JUI - Jersey Udder Index',
        Jpi: 'JPI - Jersey Performance Index',
        Cfp: 'CFP - Combined Fat Protein',
        Ecm: 'ECM - Energy Corrected Milk'
    };

    // Total absolute sum of weights (for warning)
    const totalAbs = Object.values(customIndexWeights || {}).reduce((s, v) => s + Math.abs(Number(v) || 0), 0);
    // Determine min/max for CustomIndex slider using the global pre-filter bounds from context
    const { filteredBullData, customIndexBounds } = useContext(BullGridContext);
    let customMin = 0, customMax = 0;
    if (Array.isArray(customIndexBounds) && customIndexBounds.length === 2 && !(customIndexBounds[0] === 0 && customIndexBounds[1] === 0)) {
        [customMin, customMax] = customIndexBounds;
    } else if (Array.isArray(filteredBullData) && filteredBullData.length > 0) {
        const values = filteredBullData.map(r => Number(r.CustomIndexValue) || 0).filter((_, i) => i !== filteredBullData.length - 1); // exclude average
        if (values.length > 0) {
            customMin = Math.min(...values);
            customMax = Math.max(...values);
        }
    }

    return (
        <div className='BullFilter-CustomIndex p-2'>
            <Form.Group className='mb-2 mt-0'>
                <Form.Check className='ColumnSection-Checkbox' type='switch' id='useCustomIndexToggle' label='Enable Custom Index' checked={useCustomIndex} onChange={(e) => updateUseCustomIndex(e.target.checked)} name='useCustomIndex' />
            </Form.Group>
            <div className='mb-2'>
                <Button variant='outline-secondary' size='sm' onClick={resetCustomIndexWeights} disabled={!useCustomIndex}>Reset Weights</Button>
            </div>

            {totalAbs > 100 && (
                <Alert variant='warning' className='py-1 my-2'>
                    The sum of your custom weights is greater than 100. The resulting index is not accurate.
                </Alert>
            )}

            {totalAbs > 0 && totalAbs < 100 && (
                <Alert variant='warning' className='py-1 my-2'>
                    The sum of your custom weights is less than 100.  The resulting index is not accurate.
                </Alert>
            )}

            {/* Slider to filter the computed Custom Index column */}
            <BullSlider
                sliderChange={(e, newValue) => handleSliderInputChange(e, newValue, 'CustomIndex')}
                header={'Custom Index Range'}
                name={'CustomIndex'}
                value={Array.isArray(bullFilter?.CustomIndex) ? bullFilter.CustomIndex : [customMin, customMax]}
                min={customMin}
                max={customMax}
                step={1}
            />
            {
                (Array.isArray(customIndexTraitOrder) ? customIndexTraitOrder : Object.keys(customIndexWeights)).map((trait) => (
                    <Form.Group className='mb-2' controlId={`weight-${trait}`} key={trait}>
                        <Form.Label className={`mb-0 ${theme}`}>{aliasLabels[trait] || trait}</Form.Label>
                        <InputGroup>
                            <Button variant='outline-secondary' size='sm' disabled={!useCustomIndex} aria-label={`decrement-${trait}`} onClick={() => updateCustomIndexWeight(trait, (customIndexWeights[trait] || 0) - 1)}>−</Button>
                            <Form.Control disabled={!useCustomIndex} type='number' step='1' min='-100' max='100' value={customIndexWeights[trait]} onChange={(e) => updateCustomIndexWeight(trait, Math.round(Number(e.target.value)))} />
                            <Button variant='outline-secondary' size='sm' disabled={!useCustomIndex} aria-label={`increment-${trait}`} onClick={() => updateCustomIndexWeight(trait, (customIndexWeights[trait] || 0) + 1)}>+</Button>
                        </InputGroup>
                    </Form.Group>
                ))
            }
            <Button variant='outline-secondary' size='sm' onClick={resetCustomIndexWeights}>Reset Weights</Button>
        </div>
    )
}

export default CustomIndexWeights
